import React from 'react';
import { differenceInDays, isAfter, isSameDay } from 'date-fns';
import { coerceToDate } from '@common/paymentsUtils/dateUtils';
import { formatDate } from '@common/paymentsUtils/dateUtils';

import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Heading,
  Modal,
  Paragraph,
  Table,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import type { PaymentReportHistoryInterface } from '@renter-hub/models/Types';
import { RenterOptInReportingStatusType } from '@renter-hub/models/Types';
import PaymentMethodModule from '@renter-hub/components/PaymentMethodModule';
import TotalPaymentModule from '@renter-hub/components/TotalPaymentModule';
import FeeModule from '@renter-hub/components/FeeModule';
import TransactionConstants from '@common/constants/TransactionConstants';
import ONE_TIME_PAYMENT_TYPE from '@common/constants/oneTimePaymentTypes';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { capitalizeFirstLetter } from '@common/utils/stringUtils';
import browserUtils from '@common/utils/browserUtils';
import { HiddenText } from '@common/components/HiddenText/HiddenText';
import MoveInCostsItemsDisplay from '@renter-hub/components/MoveInCostsItemsInfo';
import { QuestionMarkIconToolTip } from './QuestionMarkIconToolTip';
import { PaymentReportingStatus } from './PaymentReportingStatus';
import type { RenterTransactionProps } from '@renter-hub/models/Types';
import { logError } from '@zg-rentals/log-error';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import WrappedPaymentHistoryPdfButton from './PaymentHistoryPdfButton';

interface TransactionLedgerProps {
  transactions?: Array<RenterTransactionProps>;
  paymentReportHistory?: PaymentReportHistoryInterface;
  landlordName?: string;
  scheduledDate: string;
  currentScheduleMonth: {
    scheduleMonth: string;
    dueDate: string;
  };
  displayHiddenText?: boolean;
  openManualPaymentModal: (scheduleMonth: string) => void;
  payLateRentPaymentOnClick: (scheduleMonth: string) => void;
  showOutstandingBalanceCard: boolean;
  enablePaymentStatus?: boolean;
  enableCreditTracking?: boolean;
}

class TransactionLedger extends React.Component<TransactionLedgerProps> {
  static defaultProps = {
    scheduledDate: null,
    openManualPaymentModal: (): void => {},
    payLateRentPaymentOnClick: (): void => {},
    currentScheduleMonth: {
      scheduleMonth: '',
      dueDate: '',
    },
    showOutstandingBalanceCard: false,
    enableCreditTracking: false,
  };

  openManualPaymentModal = (scheduleMonth: string) => (): void => this.props.openManualPaymentModal(scheduleMonth);

  renderBadge(renterStatus: string, dateReceived?: string): React.ReactNode {
    let type;
    let label;

    switch (renterStatus) {
      case TransactionConstants.STATUS.COMPLETE:
        label = 'Paid';
        type = 'success';
        break;
      case TransactionConstants.STATUS.MANUAL:
        label =
          getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false) && dateReceived ? 'Marked as paid' : 'Paid';
        type = 'success';
        break;
      case TransactionConstants.STATUS.PAYMENT_FAILED:
      case TransactionConstants.STATUS.UNPAID:
      case TransactionConstants.STATUS.PAST_DUE:
      case TransactionConstants.STATUS.REFUNDED:
        label = 'Unpaid';
        type = 'critical';
        break;
      case TransactionConstants.STATUS.DISPUTED:
        label = 'Disputed';
        type = 'warning';
        break;
      default:
        logError({
          error: new Error('Transaction status not handled.'),
          context: { renterStatus },
        });
        return null;
    }

    return (
      <Tag css={{ whiteSpace: 'nowrap' }} tone={type}>
        {label}
      </Tag>
    );
  }

  renderOneTimePaymentLabel = (paymentType: string, paymentPlanInstallmentTitle = ''): string => {
    switch (paymentType) {
      case ONE_TIME_PAYMENT_TYPE.OTHER:
        return `${capitalizeFirstLetter(paymentType)} payment`;
      case ONE_TIME_PAYMENT_TYPE.LATE_FEE:
        return 'Late fee';
      case ONE_TIME_PAYMENT_TYPE.PET_FEE:
        return 'Pet fee';
      case ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN:
        return paymentPlanInstallmentTitle;
      default:
        return capitalizeFirstLetter(paymentType);
    }
  };

  renderTransactionTitle = (transaction): string => {
    if (transaction.paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT) {
      return transaction.rentMonthHeading;
    } else if (transaction.paymentCategory === TransactionConstants.CATEGORY.MOVE_IN_COSTS) {
      return 'Move-in payment';
    }
    const { paymentType, paymentPlanInstallmentTitle } = transaction;
    return this.renderOneTimePaymentLabel(paymentType, paymentPlanInstallmentTitle);
  };

  renderPayNowButton = (scheduleMonth: string) => {
    if (this.props.showOutstandingBalanceCard) {
      return (
        <Button
          size="sm"
          css={{ marginRight: 'tighter' }}
          onClick={() => {
            this.props.payLateRentPaymentOnClick(scheduleMonth);
          }}
        >
          Pay now
        </Button>
      );
    }
    return <></>;
  };

  getRentPaymentRowBody = (transaction) => {
    const { landlordName, currentScheduleMonth, scheduledDate } = this.props;
    const {
      scheduleMonth: transactionScheduleMonth,
      dateCreated,
      dateReceived,
      zgFee,
      totalAmount,
      accountName,
      accountLast4,
      paymentMethod,
      renterStatus,
      markAsPaidEligible,
    } = transaction;

    // Renters can mark the month paid for all past due dates and for currentDueDate if not scheduled
    const currentMonth = coerceToDate(currentScheduleMonth.scheduleMonth) as Date;
    const txnMonth = coerceToDate(transactionScheduleMonth) as Date;
    const transactionDueDateIsPast = isAfter(currentMonth, txnMonth);
    const transactionDueDateIsCurrent = isSameDay(currentMonth, txnMonth);
    const canMarkMonthAsPaid = transactionDueDateIsPast || (transactionDueDateIsCurrent && !scheduledDate);

    const datePaid = formatDate(dateCreated, 'MMMM do, yyyy');

    if (renterStatus === TransactionConstants.STATUS.COMPLETE || renterStatus === TransactionConstants.STATUS.PENDING) {
      return (
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' },
            gap: '16px',
          }}
        >
          <Box>
            <Paragraph textStyle="body-bold">Paid to</Paragraph>
            <Text className="fs-exclude">
              {landlordName} on {datePaid}
            </Text>
          </Box>
          <Box>
            <PaymentMethodModule accountName={accountName} accountLast4={accountLast4} />
          </Box>
          {paymentMethod === 'card' && (
            <Box>
              <FeeModule fee={zgFee} />
            </Box>
          )}
          {paymentMethod === 'card' && (
            <Box>
              <TotalPaymentModule totalAmount={totalAmount} />
            </Box>
          )}
        </Box>
      );
    }

    if (renterStatus === TransactionConstants.STATUS.MANUAL) {
      const markedByLandlord = Boolean(dateReceived);
      return markedByLandlord ? (
        <Paragraph textStyle="body-sm">
          Your landlord recorded this payment as received outside of Zillow on{' '}
          {formatDate(dateReceived, 'MMMM do, yyyy')}.
        </Paragraph>
      ) : (
        <Paragraph textStyle="body-sm">
          You’ve taken care of this payment with your landlord outside of Zillow.
        </Paragraph>
      );
    }

    if (canMarkMonthAsPaid && markAsPaidEligible === false) {
      return (
        <>
          <Paragraph css={{ marginBottom: 'default' }} textStyle="body-sm">
            Catch up on your late payments today. The landlord requires all the rent payments for this property to be
            made on Zillow Payments. Have questions?{' '}
            <Anchor
              href="https://zillow.zendesk.com/hc/en-us/sections/360000067847-Online-Rent-Payments"
              target="_blank"
              rel="noopener noreferrer"
            >
              See our help center
            </Anchor>
          </Paragraph>
          {this.renderPayNowButton(transactionScheduleMonth)}
        </>
      );
    } else if (canMarkMonthAsPaid) {
      const isLandlordMarkAsPaidOfflineEnabled = getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false);
      const payNowButton = this.renderPayNowButton(transactionScheduleMonth);
      const markAsPaidButton = !isLandlordMarkAsPaidOfflineEnabled ? (
        <TextButton onClick={this.openManualPaymentModal(transactionScheduleMonth)}>Mark this month as paid</TextButton>
      ) : null;
      const hasBothActions = this.props.showOutstandingBalanceCard && !isLandlordMarkAsPaidOfflineEnabled;
      return (
        <>
          <Paragraph css={{ marginBottom: 'default' }} textStyle="body-sm">
            {isLandlordMarkAsPaidOfflineEnabled
              ? 'Catch up on your late payments today. If you’ve already paid your landlord outside of Zillow, only your landlord can mark this payment as paid.'
              : 'Catch up on your late payments today. If you’ve taken care of this payment with your landlord outside of Zillow you can mark it as paid to keep track of it here.'}
          </Paragraph>
          {hasBothActions ? (
            <ButtonGroup aria-label="Payment actions" size="sm">
              {payNowButton}
              {markAsPaidButton}
            </ButtonGroup>
          ) : (
            <>
              {payNowButton}
              {markAsPaidButton}
            </>
          )}
        </>
      );
    }

    return <Paragraph>Your rent for this month has not been paid yet.</Paragraph>;
  };

  getOneTimePaymentRowBody = (transaction) => {
    const { landlordName } = this.props;
    const {
      paymentType,
      renterStatus,
      notes = '',
      dateCreated,
      dateReceived,
      paymentMethod,
      totalAmount,
      zgFee,
      accountName,
      accountLast4,
    } = transaction;
    const datePaid = formatDate(dateCreated, 'MMMM do, yyyy');

    if (renterStatus === TransactionConstants.STATUS.COMPLETE || renterStatus === TransactionConstants.STATUS.PENDING) {
      return (
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' },
            gap: '16px',
          }}
        >
          <Box>
            <Paragraph textStyle="body-bold">Sent to</Paragraph>
            <Text>
              {landlordName} on {datePaid}
            </Text>
          </Box>
          <Box>
            <Paragraph textStyle="body-bold">Notes</Paragraph>
            <Text>{notes || paymentType}</Text>
          </Box>
          {paymentMethod === 'card' && (
            <Box>
              <FeeModule fee={zgFee} />
            </Box>
          )}
          {paymentMethod === 'card' && (
            <Box>
              <TotalPaymentModule totalAmount={totalAmount} />
            </Box>
          )}
          <Box>
            <PaymentMethodModule accountName={accountName} accountLast4={accountLast4} />
          </Box>
        </Box>
      );
    }

    if (renterStatus === TransactionConstants.STATUS.MANUAL) {
      const markedByLandlord = Boolean(dateReceived);
      return (
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          {markedByLandlord ? (
            <Paragraph textStyle="body-sm">
              Your landlord recorded this payment as received outside of Zillow on{' '}
              {formatDate(dateReceived, 'MMMM do, yyyy')}.
            </Paragraph>
          ) : (
            <Paragraph textStyle="body-sm">
              You’ve taken care of this payment with your landlord outside of Zillow.
            </Paragraph>
          )}
          <Box>
            <Paragraph textStyle="body-bold">Notes</Paragraph>
            <Text>{notes || paymentType}</Text>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <Paragraph textStyle="body-bold">Notes</Paragraph>
        <Text>{notes || paymentType}</Text>
      </Box>
    );
  };

  getMoveInCostsRowBody = (transaction) => {
    const { landlordName } = this.props;
    const { dateCreated, dateReceived, zgFee, totalAmount, moveInCostsItems, accountName, accountLast4, renterStatus } =
      transaction;
    const datePaid = formatDate(dateCreated, 'MMMM do, yyyy');
    if (renterStatus === TransactionConstants.STATUS.COMPLETE) {
      return (
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'default',
          }}
        >
          <Box>
            <Paragraph textStyle="body-bold">Paid to</Paragraph>
            <Text className="fs-exclude">
              {landlordName} on {datePaid}
            </Text>
          </Box>
          <PaymentMethodModule accountName={accountName} accountLast4={accountLast4} />
          <MoveInCostsItemsDisplay zgFee={zgFee} totalAmount={totalAmount} items={moveInCostsItems} />
        </Box>
      );
    }

    if (renterStatus === TransactionConstants.STATUS.MANUAL) {
      const markedByLandlord = Boolean(dateReceived);
      return (
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'default',
          }}
        >
          {markedByLandlord ? (
            <Paragraph textStyle="body-sm">
              Your landlord recorded this payment as received outside of Zillow on{' '}
              {formatDate(dateReceived, 'MMMM do, yyyy')}.
            </Paragraph>
          ) : (
            <Paragraph textStyle="body-sm">
              You’ve taken care of this payment with your landlord outside of Zillow.
            </Paragraph>
          )}
          <MoveInCostsItemsDisplay zgFee={zgFee} totalAmount={totalAmount} items={moveInCostsItems ?? []} />
        </Box>
      );
    }

    // Genuinely-unpaid move-in (UNPAID / PAST_DUE). renderRows only surfaces move-in costs in
    // paid or unpaid statuses, so edge statuses (refunded/disputed/failed) never reach this copy.
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'default',
        }}
      >
        <Paragraph textStyle="body-sm">Your move-in payment has not been paid yet.</Paragraph>
        <MoveInCostsItemsDisplay zgFee={zgFee} totalAmount={totalAmount} items={moveInCostsItems ?? []} />
      </Box>
    );
  };

  renderRows = (transactions, isMobile, showPaymentStatus) =>
    transactions
      .filter((transaction) => {
        const { paymentCategory, renterStatus } = transaction;
        // Show move-in costs only in the statuses we render a body for (paid or genuinely unpaid).
        // Edge statuses (refunded/disputed/failed) fall through to the safety net below rather than
        // rendering an inaccurate "not paid yet" row.
        const isDisplayableMoveInCost =
          paymentCategory === TransactionConstants.CATEGORY.MOVE_IN_COSTS &&
          [
            TransactionConstants.STATUS.COMPLETE,
            TransactionConstants.STATUS.MANUAL,
            TransactionConstants.STATUS.UNPAID,
            TransactionConstants.STATUS.PAST_DUE,
          ].includes(renterStatus);
        if (
          paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT ||
          paymentCategory === TransactionConstants.CATEGORY.ONE_TIME_PAYMENT ||
          isDisplayableMoveInCost
        ) {
          return true;
        }

        logError({
          error: new Error('This transaction cannot be rendered in the payment ledger.'),
          context: { transaction },
        });

        return false;
      })
      .map((transaction, i) => {
        const { paymentReportHistory, currentScheduleMonth } = this.props;
        const { paymentCategory, amount, renterStatus } = transaction;
        const formattedAmountToDisplay = centsToDollars(amount, { style: 'short' });

        let rowBody;
        if (paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT) {
          rowBody = this.getRentPaymentRowBody(transaction);
        } else if (paymentCategory === TransactionConstants.CATEGORY.ONE_TIME_PAYMENT) {
          rowBody = this.getOneTimePaymentRowBody(transaction);
        } else if (paymentCategory === TransactionConstants.CATEGORY.MOVE_IN_COSTS) {
          rowBody = this.getMoveInCostsRowBody(transaction);
        }

        return (
          <Table.Row key={i.toString()}>
            <Table.Cell>
              <Paragraph>{this.renderTransactionTitle(transaction)}</Paragraph>
              {isMobile && showPaymentStatus && this.renderBadge(renterStatus, transaction.dateReceived)}
            </Table.Cell>
            {(!isMobile || !showPaymentStatus) && (
              <Table.Cell>{this.renderBadge(renterStatus, transaction.dateReceived)}</Table.Cell>
            )}
            <Table.Cell>
              <Text textStyle="body-bold">{formattedAmountToDisplay}</Text>
            </Table.Cell>
            {showPaymentStatus && (
              <Table.Cell>
                <PaymentReportingStatus
                  transaction={transaction}
                  paymentReportHistory={paymentReportHistory}
                  currentScheduleMonth={currentScheduleMonth}
                />
              </Table.Cell>
            )}
            <Table.Cell>
              <Modal
                header={<Heading level={2}>{this.renderTransactionTitle(transaction) || 'Payment details'}</Heading>}
                body={rowBody}
                shouldAwaitInteractionResponse
                size={{ base: 'fullScreen', lg: 'xs' }}
                trigger={<TextButton>{isMobile ? 'View' : 'View more'}</TextButton>}
              />
            </Table.Cell>
          </Table.Row>
        );
      });

  renderEmptyLedger = () => (
    <>
      <Paragraph textStyle="body-bold">Payment History</Paragraph>
      <Paragraph css={{ color: 'text.subtle', marginBottom: 'loose' }}>
        After you’ve made your first payment, your history will appear here.
      </Paragraph>
    </>
  );

  renderDefaultLedger = () => {
    const { displayHiddenText } = this.props;
    return (
      <>
        <Paragraph textStyle="body-bold">Payment History</Paragraph>
        <Table.Row key={1}>
          <Table.Cell>
            <HiddenText visible={displayHiddenText} />
          </Table.Cell>
          <Table.Cell>
            <HiddenText visible={displayHiddenText} />
          </Table.Cell>
        </Table.Row>
        <Table.Row key={2}>
          <Table.Cell>
            <HiddenText visible={displayHiddenText} />
          </Table.Cell>
          <Table.Cell>
            <HiddenText visible={displayHiddenText} />
          </Table.Cell>
        </Table.Row>
      </>
    );
  };

  filterTransactions = (transactions) =>
    transactions
      .filter((transaction) => {
        // Hide unpaid rent for future schedule months (after the current month). Current-month and
        // past-due unpaid rent still render, matching the "Your Rental" view.
        if (
          transaction.paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT &&
          transaction.renterStatus === TransactionConstants.STATUS.UNPAID &&
          isAfter(
            coerceToDate(transaction.scheduleMonth) as Date,
            coerceToDate(this.props.currentScheduleMonth.scheduleMonth) as Date,
          )
        ) {
          return false;
        }

        return true;
      })
      .sort((transactionA, transactionB) => {
        // Sort transactions by due date (latest transaction appears first)
        const diff = differenceInDays(
          coerceToDate(transactionB.dueDate) as Date,
          coerceToDate(transactionA.dueDate) as Date,
        );
        if (diff < 0) {
          return -1;
        } else if (diff > 0) {
          return 1;
        }
        return 0;
      });

  render(): React.ReactNode {
    const { transactions = [], displayHiddenText } = this.props;
    const filteredTransactions = this.filterTransactions(transactions);
    const isMobile = browserUtils.screen.type() === browserUtils.screenType.MOBILE;
    const showPaymentStatus =
      this.props.enablePaymentStatus &&
      !this.props.enableCreditTracking &&
      this.props.paymentReportHistory?.paymentReportingStatus === RenterOptInReportingStatusType.ACTIVE;
    const reportingStatusColumnName = isMobile ? 'Reporting status' : 'Rent reporting status';

    const reportingStatusToolTipContent = (
      <>
        <Text style={{ textTransform: 'none' }}>
          When you pay rent on time, your payments are reported to Experian, Equifax, and TransUnion.
        </Text>
      </>
    );

    if (displayHiddenText) {
      return this.renderDefaultLedger();
    } else if (!filteredTransactions.length) {
      return this.renderEmptyLedger();
    }

    return (
      <div>
        <WrappedPaymentHistoryPdfButton />
        <Table.Responsive>
          <Table.Root
            appearance="horizontal"
            aria-label="Transactions table"
            size="md"
            alignments={
              showPaymentStatus ? ['start', 'start', 'center', 'center', 'end'] : ['start', 'start', 'center', 'end']
            }
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Payment</Table.HeaderCell>
                {(!isMobile || !showPaymentStatus) && <Table.HeaderCell>Payment status</Table.HeaderCell>}
                <Table.HeaderCell>Amount</Table.HeaderCell>
                {showPaymentStatus && (
                  <Table.HeaderCell>
                    {reportingStatusColumnName} <QuestionMarkIconToolTip content={reportingStatusToolTipContent} />
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            {this.renderRows(filteredTransactions, isMobile, showPaymentStatus)}
          </Table.Root>
        </Table.Responsive>
      </div>
    );
  }
}

export default TransactionLedger;
